import { ReactElement, Reducer, createContext, useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Auth0Client, User as CAuth0User, CacheLocation } from '@auth0/auth0-spa-js';
import * as Sentry from '@sentry/react';
import { useUnleashContext } from '@unleash/proxy-client-react';
import axios from 'axios';
import { SplashScreen } from 'components/fallback/SplashScreen';
import { useAmplitude } from 'hooks/useAmplitude';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { OptionsObject, SnackbarKey, useSnackbar } from 'notistack';
import { GreenerProjectsUser, GreenerUser } from 'types';
import { BASEURL } from '../utils/constants';
import { keysToCamelCase } from '../utils/formatters';
import { GetMeResponse } from './contexts.types';

type ReducerAction =
  | { type: 'INITIALISE'; payload?: ReducerState }
  | { type: 'LOGIN'; payload?: ReducerState }
  | { type: 'LOGOUT'; payload?: ReducerState }
  | { type: 'UPDATE_USER'; payload?: ReducerState };

type ReducerState = {
  isAuthenticated?: boolean;
  isInitialised?: boolean;
  user: GreenerProjectsUser | null; // Make sure user can be null
};

const initialAuthState: ReducerState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const authClientVars = {
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID as string,
  domain: import.meta.env.VITE_AUTH0_DOMAIN as string,
  cacheLocation: 'localstorage' as CacheLocation,
  useRefreshTokensFallback: true,
  authorizationParams: {
    redirect_uri: window.location.origin,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
    ui_locales: 'en nl',
    responseMode: 'token',
  },
};

const auth0Client: Auth0Client = new Auth0Client(authClientVars);

const mapGreenerUser = (greenerUser: GreenerUser) => {
  return {
    id: greenerUser.userId,
    email: greenerUser.email,
    username: `${greenerUser.firstName} ${greenerUser.lastName}`,
    firstName: greenerUser.firstName,
    lastName: greenerUser.lastName,
    phone: greenerUser.phone,
    organisation: greenerUser.organisation,
    country: greenerUser.country,
  };
};

const mapUserInfo = (greenerUser: GreenerUser, auth0User: CAuth0User, auth0pPermissions: string[]) => {
  const userRole = greenerUser.userRole || {};

  auth0pPermissions.forEach((permission) => {
    permission = permission.replace('role:', '');
    permission = permission.toLowerCase().replace(/[-_][a-z]/g, (group) => group.slice(-1).toUpperCase());

    if (!(permission in userRole)) {
      // this condition is a back door for e2e tests to set permissions like 'permissionName: false' to disable existing permission
      userRole[permission] = true;
    }
  });

  return {
    avatar: auth0User.picture,
    auth0Id: auth0User.sub,
    role: userRole,
    ...mapGreenerUser(greenerUser),
  };
};

const getMe = async (): Promise<GetMeResponse> => {
  const token = await getTokenSilently();

  const response = await axios.get(BASEURL + '/users/me/', {
    headers: { Authorization: 'Bearer ' + token },
  });

  const { user_role, ...rest } = response.data;
  const me = { ...keysToCamelCase(rest), userRole: user_role } as GreenerUser;
  return { me };
};

// When user is authenticated get all info and put in context
const getAndSetUserInfo = async (
  auth0Client: Auth0Client,
  logout: () => void,
  dispatch: (action: ReducerAction) => void,
  enqueueSnackbar: (message: string, options: OptionsObject) => SnackbarKey,
  t: (key: string) => string,
  setUserId: (userId: string) => Promise<void>
) => {
  const auth0User = await auth0Client?.getUser();
  const auth0Permissions = await getPermissionsFromToken();

  let greenerUser: GreenerUser | null = null;
  try {
    const { me } = await getMe();
    greenerUser = me;
  } catch (e) {
    Sentry.captureException(e);
    if (isSessionExpiredError(e)) {
      logout();
    }
    enqueueSnackbar(t('auth:failedLogin'), { variant: 'error' });
    dispatch({ type: 'INITIALISE', payload: { isAuthenticated: false, user: null } });
    return;
  }
  // It all worked, put the user-info in the context
  if (auth0User && greenerUser) {
    const mappedUser = mapUserInfo(greenerUser, auth0User, auth0Permissions);

    dispatch({
      type: 'INITIALISE',
      payload: {
        isAuthenticated: true,
        user: mappedUser,
      },
    });
    await setUserId(mappedUser.email);
  }
};

const reducer = (state: ReducerState, action: ReducerAction) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload as ReducerState;
      //Set the user data on the sentry context so we can use it in the error reporting
      user &&
        Sentry.setUser({
          name: `${user.firstName} ${user.lastName}`,
          ...user,
        } as CAuth0User);

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    case 'LOGIN': {
      const { user } = action.payload as ReducerState;

      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        isInitialised: false,
        user: null,
      };
    }
    case 'UPDATE_USER': {
      const updatedUser = action.payload?.user as GreenerProjectsUser;

      return {
        ...state,
        user: updatedUser ? { ...state.user, ...updatedUser } : state.user,
      };
    }
    default: {
      return state;
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: 'Auth0',
  login: () => Promise.resolve(),
  logout: () => null,
  auth0Client,
  getTokenSilently: () => Promise.resolve(''),
  updateUserContext: () => Promise.resolve(), // Update the type if necessary
});

const getPermissionsFromToken = async () => {
  const token: string = await getTokenSilently();
  const decoded: JwtPayload = jwtDecode(token);
  const permissions: string[] | undefined = decoded.permissions;
  if (typeof permissions == 'undefined') {
    return [];
  }
  return permissions;
};

const requiresInteractiveLogin = (e: unknown) => {
  const code = (e as { error: string }).error;
  return code === 'login_required' || code === 'consent_required';
};

const isSessionExpiredError = (e: unknown) =>
  requiresInteractiveLogin(e) || (axios.isAxiosError(e) && e.response?.status === 401);

const getTokenSilently = async () => {
  try {
    return await auth0Client?.getTokenSilently();
  } catch (e) {
    // See https://community.auth0.com/t/getaccesstokensilently-throws-error-login-required/52333/3
    // Race conditions (which empirically seem to be browser-specific) can cause the `getTokenSilently` method to be
    // called before `loginWithRedirect`. This is always wrong and leads to inconsistent authentication state.
    // consent_required occurs when running locally against a remote Auth0 tenant that requires interactive consent.
    // In both cases, redirect to login so the user can authenticate/grant consent interactively.
    if (requiresInteractiveLogin(e)) {
      await auth0Client?.loginWithRedirect({
        appState: { returnTo: location.pathname },
      });
    }
    throw e;
  }
};

export const AuthProvider = ({ children }: { children: ReactElement }) => {
  // noinspection JSCheckFunctionSignatures
  const [state, dispatch] = useReducer<Reducer<ReducerState, ReducerAction>>(reducer, initialAuthState);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { setUserId } = useAmplitude();
  const updateUnleashContext = useUnleashContext();

  const updateUserId = useCallback(
    async (userId: string) => {
      setUserId(userId);
      await updateUnleashContext({ userId: userId });
    },
    [updateUnleashContext, setUserId]
  );

  const login = async () => {
    if (!auth0Client) {
      return;
    }
    await auth0Client.loginWithRedirect();
  };

  const logout = useCallback(() => {
    if (!auth0Client) {
      return null;
    }

    dispatch({
      type: 'LOGOUT',
    });

    auth0Client.logout({
      logoutParams: {
        returnTo: import.meta.env.VITE_AUTH0_REDIRECT_URI,
      },
    });
    return null;
  }, []);

  // Get the latest version of the user and put in the state
  const updateUserContext = async () => {
    try {
      const { me: greenerUser } = await getMe();
      const auth0Permissions = await getPermissionsFromToken();
      if (greenerUser) {
        dispatch({
          type: 'UPDATE_USER',
          payload: {
            user: mapUserInfo(greenerUser, auth0Client.getUser(), auth0Permissions),
          },
        });
      }
    } catch (e) {
      if (isSessionExpiredError(e)) {
        Sentry.captureException(e);
        logout();
      }
    }
  };

  useEffect(() => {
    const initialise = async () => {
      try {
        await auth0Client.checkSession();
      } catch (e) {
        // consent_required means Auth0 needs interactive consent (e.g. local dev against staging).
        // Redirect to the login page so the user can grant consent.
        if (requiresInteractiveLogin(e)) {
          await auth0Client.loginWithRedirect({
            appState: { returnTo: location.pathname },
          });
          return;
        }
        throw e;
      }
      const query = window.location.search;

      if (query.includes('code=') && query.includes('state=')) {
        try {
          const { appState } = await auth0Client.handleRedirectCallback();
          window.location.replace(appState?.returnTo ?? '/');
        } catch (e) {
          console.error('Auth0 redirect callback error:', e);
        }
      }

      const isAuthenticated = await auth0Client.isAuthenticated();
      if (isAuthenticated) {
        await getAndSetUserInfo(auth0Client, logout, dispatch, enqueueSnackbar, t, updateUserId);
      } else {
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated,
            user: null,
          },
        });
      }
    };

    initialise();
  }, [enqueueSnackbar, dispatch, logout, t, updateUserId]);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'Auth0',
        login,
        logout,
        auth0Client,
        getTokenSilently,
        updateUserContext,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
